import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { CashBookResponse, DayLogsDTO, Page } from '@six-pockets/shared';
import { cashBookApi } from '@/api';
import { PencilIcon, PlusIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, InlineAlert, Pagination, PeriodPicker, Tabs, type TabOption } from '@/components/ui';
import { BankDepositModal } from '@/features/ledgers/components/BankDepositModal';
import { CashBookRow } from '@/features/ledgers/components/CashBookRow';
import { CounterLogFormModal } from '@/features/ledgers/components/CounterLogFormModal';
import { CounterLogRow } from '@/features/ledgers/components/CounterLogRow';
import { selectionPhrase, thisMonthKey } from '@/features/reports/utils';
import { formatDateTime, formatINR } from '@/utils/format';
import styles from '@/styles/pages/CashBookPage/style.scss';

const PAGE_SIZE = 12;

type CashBookView = 'passbook' | 'logs';

const VIEW_OPTIONS: TabOption<CashBookView>[] = [
  { value: 'passbook', label: 'Passbook' },
  { value: 'logs', label: 'Logs' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Cash book: the cash counter kept like a bank passbook. Two cards on top (money at the cash counter now; taken for
 * bank deposit in the period), then two views. Passbook: every line of the picked months, newest first, not paged:
 * the day's cash count (as the change since the book's last balance), deposits to the bank and expenses paid from
 * the cash counter, each with the balance after it. Logs: one row per day with the cash and UPI counts staff logged,
 * 12 a page. The header
 * logs today's counts and takes a deposit. The tab is in the URL (?view=logs).
 */
export function CashBookPage() {
  const [params, setParams] = useSearchParams();
  const view: CashBookView = params.get('view') === 'logs' ? 'logs' : 'passbook';

  const [period, setPeriod] = useState(() => ({ from: thisMonthKey(), to: thisMonthKey() }));
  const { from, to } = period;
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [book, setBook] = useState<CashBookResponse | null>(null);
  const [logs, setLogs] = useState<Page<DayLogsDTO> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A day's logs to add to or correct (today's from the header, any day's from its row; the modal can change the day);
  // 'deposit' takes cash to the bank.
  const [editing, setEditing] = useState<DayLogsDTO | 'deposit' | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    cashBookApi.get({ from, to }, ctrl.signal).then(
      (b) => {
        setBook(b);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [from, to, reloadKey]);

  useEffect(() => {
    if (view !== 'logs') return;
    const ctrl = new AbortController();
    cashBookApi.logs({ from, to, page, pageSize: PAGE_SIZE }, ctrl.signal).then(setLogs, (err) => {
      if (!ctrl.signal.aborted) setError(messageOf(err));
    });
    return () => ctrl.abort();
  }, [view, from, to, page, reloadKey]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setModalError(null);
    try {
      await fn();
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
    setModalError(null);
  };

  const changeView = (v: CashBookView) => setParams(v === 'passbook' ? {} : { view: v }, { replace: true });
  const changePeriod = (f: string, t: string) => {
    setPeriod({ from: f, to: t });
    setPage(1);
  };

  const onEdit = useCallback((l: DayLogsDTO) => setEditing(l), []);

  const phrase = selectionPhrase('month', from, to);
  const entries = book?.entries ?? [];

  return (
    <>
      <PageHeader
        title="Cash book"
        aside={
          book && (
            <div className={styles.headerActions}>
              <Button variant="secondary" onClick={() => setEditing('deposit')}>
                Deposit to bank
              </Button>
              {book.today.cash || book.today.upi ? (
                <Button variant="secondary" icon={<PencilIcon size={16} />} onClick={() => setEditing(book.today)}>
                  Edit today's count
                </Button>
              ) : (
                <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setEditing(book.today)}>
                  Add count
                </Button>
              )}
            </div>
          )
        }
      />

      {book && (
        <section className={styles.cards} aria-label="Cash book summary">
          <div className={styles.card}>
            <span className={styles.cardLabel}>Money at cash counter</span>
            <span className={styles.cardValue}>{book.atCounter === null ? '—' : formatINR(book.atCounter)}</span>
            <span className={styles.cardNote}>
              {book.lastCountedAt ? `Last counted ${formatDateTime(book.lastCountedAt)}` : 'No cash count logged yet'}
            </span>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Taken for bank deposit {phrase}</span>
            <span className={styles.cardValue}>{formatINR(book.deposited)}</span>
            <span className={styles.cardNote}>{plural(book.depositCount, 'deposit', 'deposits')}</span>
          </div>
        </section>
      )}

      <Tabs
        label="Cash book"
        options={VIEW_OPTIONS}
        value={view}
        onChange={changeView}
        aside={<PeriodPicker granularity="month" from={from} to={to} onChange={changePeriod} label="Period" />}
      />

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {view === 'passbook' ? (
        !book && loading ? (
          <p className={styles.empty}>Loading the passbook…</p>
        ) : entries.length === 0 ? (
          <p className={styles.empty}>Nothing in the passbook {phrase}</p>
        ) : (
          <>
            <span className={styles.count}>
              {plural(entries.length, 'transaction', 'transactions')} {phrase}
            </span>
            <ul className={styles.list} aria-busy={loading}>
              {entries.map((e) => (
                <CashBookRow key={`${e.kind}-${e.id}`} entry={e} />
              ))}
            </ul>
          </>
        )
      ) : !logs ? (
        <p className={styles.empty}>Loading logs…</p>
      ) : logs.items.length === 0 ? (
        <p className={styles.empty}>Nothing logged {phrase}</p>
      ) : (
        <>
          <span className={styles.count}>
            {plural(logs.total, 'day', 'days')} logged {phrase}
          </span>
          <ul className={styles.list}>
            {logs.items.map((l) => (
              <CounterLogRow key={l.day} logs={l} onEdit={onEdit} />
            ))}
          </ul>
          <Pagination page={logs.page} pageSize={logs.pageSize} total={logs.total} onPageChange={setPage} />
        </>
      )}

      {editing === 'deposit' && book && (
        <BankDepositModal
          atCounter={book.atCounter}
          busy={busy}
          error={modalError}
          onSave={(body) => void run(() => cashBookApi.deposit(body))}
          onClose={closeModal}
        />
      )}

      {editing && editing !== 'deposit' && book && (
        <CounterLogFormModal
          key={editing.day}
          logs={editing}
          today={book.today.day}
          busy={busy}
          error={modalError}
          onSave={(day, body) => void run(() => cashBookApi.saveDay(day === book.today.day ? 'today' : day, body))}
          onClose={closeModal}
        />
      )}
    </>
  );
}
